// The report's own references, as printed, at the head of the Test pane (TGCLOUD-1868, plan A6;
// sub-task 5): "Lab report number, as printed: NB26-PN-00416", each pointing to where the report
// prints it.
//
// They are how a clinician checks that the report beside the Test is the one the Lab Report Inbox
// matched: the inbox item shows the same references (lab-report-inbox/ReportSays.jsx, with the same
// labels). The answer carries them for display only (`reportKeys`, backend labReportIntake.js):
// never in the proposal, the Test or the lab report record. A reference is about no Test field, so
// it is selected in the report by its own path (`reportKeys.<i>`), not through a value's mark.
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import {
  REPORT_KEY_LABELS,
  labelOf
} from 'src/components/lab-report-inbox/labReportInboxVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useVerification } from './useVerificationView';
import {
  SHOW_IN_REPORT,
  reportKeyText
} from './labReportVerificationVocabulary';

const hasText = (v) => typeof v === 'string' && v.trim() !== '';

/** A reference's label: the report's own words for it, else the inbox's for its kind. */
export const reportKeyLabelOf = (key) =>
  (hasText(key?.label) && key.label) ||
  (hasText(key?.labelAsPrinted) && key.labelAsPrinted) ||
  labelOf(REPORT_KEY_LABELS, key?.kind);

/**
 * @param {object} props
 * @param {Array<{ kind, label?, valueAsPrinted }>} props.keys  the answer's `reportKeys`
 */
export default function ReportKeys({ keys = [] }) {
  const t = useAutoTranslation();
  const view = useVerification();
  const shown = (Array.isArray(keys) ? keys : []).filter(
    (k) => k && hasText(k.valueAsPrinted)
  );
  if (!shown.length) return null;
  // Where each is printed, while the report can be shown.
  const placed = new Set(
    view && view.active && view.reportStatus !== 'failed'
      ? (view.anchors || [])
          .filter((a) => a && Array.isArray(a.rects) && a.rects.length)
          .map((a) => a.path)
      : []
  );
  return (
    <Box data-testid="report-keys" sx={{ mb: 1 }}>
      {(Array.isArray(keys) ? keys : []).map((k, i) => {
        if (!k || !hasText(k.valueAsPrinted)) return null;
        const text = reportKeyText(reportKeyLabelOf(k), k.valueAsPrinted, t);
        const path = `reportKeys.${i}`;
        return (
          <Box
            key={path}
            data-testid="report-key"
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: 0.5
            }}
          >
            <Typography
              variant="body2"
              component="div"
              sx={{ fontWeight: 600 }}
            >
              {text}
            </Typography>
            {placed.has(path) && (
              <Button
                size="small"
                variant="text"
                startIcon={<CropFreeIcon />}
                aria-label={`${t(SHOW_IN_REPORT)}: ${reportKeyLabelOf(k)}`}
                onClick={() => view.select([path], 'value')}
                sx={{ py: 0, minHeight: 0, textTransform: 'none' }}
              >
                {t(SHOW_IN_REPORT)}
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
